import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { ReactComponent as Arrow } from './icons/arrow.svg'
import { useDetectClickOutside } from 'react-detect-click-outside'
import FloorPlanPicker, { FloorPlanPickerContent } from './FloorPlanPicker'
import { getHumanLabelBathrooms, getHumanLabelBedrooms, getLineName } from '../../../../helpers/line'
import BottomSheet from '../../../BottomSheet'
import useDetectDevice from '../../../../hooks/useDetectDevice'
import { captureEvent } from '../../../../analytics'
import { analyticsEvents } from '../../../../analytics/events'
import TickerText from './TickerText'
import { objectCopy } from '../../../../helpers/general'
import { findEntranceLevel } from '../../../../helpers/levels'
import { AppConfigContext } from '../../../../context/AppConfig'
import { variants } from '../../../../constants/variant'
import Loader from '../../../Loader'

export function LinesLoadingIndicator({ size = 16 }) {
  return (
    <div className="flex items-center w-full justify-center gap-2">
      <div
        className="animate-spin border-2 border-gray-300 border-t-black rounded-full"
        style={{ width: size, height: size }}
      ></div>
    </div>
  )
}

function LinePicker({
  className = '',
  selectedPlan,
  property,
  selectedLine,
  onLineSelected,
  onFloorPlanSelected,
  mobile = false,
  ...props
}) {
  const [toggled, setToggled] = useState(false)

  const [floorPlanPickerShown, setFloorPlanPickerShown] = useState(false)

  let { single_line, isBrodskyApp, isSFApp } = useContext(AppConfigContext)

  const ref = useDetectClickOutside({
    onTriggered: () => {
      setToggled(false)
    },
  })

  const lines = useMemo(() => {
    return property.unit_types.map((unit_type) => ({
      id: unit_type.id,
      data: unit_type,
      label: getLineName(unit_type),
    }))
  }, [property])

  const { isMobile, isDesktop, isMobileLandscape } = useDetectDevice()

  const hasFloorPlanPicker = useMemo(() => {
    const type = localStorage.getItem('sparx.widget_app.floor_plan_picker_type')
    if (type) {
      if (type === 'modal') {
        return true
      }
      if (type === 'dropdown') {
        return false
      }
    }
    if (property?.properties_configuration_options?.supports_floor_plan_screen_in_cort_widget) {
      return true
    } else {
      const linesEntranceLevels = lines.map((l) => {
        return {
          level: findEntranceLevel(l.data.unit_type_levels),
          line: l.data,
        }
      })

      function packageEntranceFloorPlans(packs) {
        console.log('packs', packs)
        return packs.map(({ pack: p, line }) => {
          const plans = p.unit_plans.filter((u) =>
            linesEntranceLevels.find((l) => {
              return l.level.id === u.unit_type_level.id && line.id === l.line.id
            }),
          )
          if(!plans.find((u) => u.contains_angle) || plans[0]){
            console.log('error: Package id', p.id, 'has no angle or first plan is not the entrance level')
          }
          // Get floor plan preferable with angle, if not, fallback to first one
          return plans.find((u) => u.contains_angle) || plans[0]
        }).filter((p) => p && p.src)
      }

      //==========================================================================
      // Most cheap packages
      //==========================================================================

      const mostCheapPackages = lines.map((line) => {
        const mostCheapPackagePrice = Math.min(...line.data.packages.map((p) => p.price))

        return {
          pack: line.data.packages.find((p) => p.price === mostCheapPackagePrice),
          line: line.data,
        }
      })
      const mostCheapPackageEntraceFloorPlans = packageEntranceFloorPlans(mostCheapPackages)
      console.log('mostCheapPackageEntraceFloorPlans', mostCheapPackageEntraceFloorPlans)
      if (lines.length > 4 && mostCheapPackageEntraceFloorPlans.every((p) => p && p.src)) {
        return true
      } else {
        return false
      }
    }
  }, [property, lines])

  useEffect(() => {
    if (floorPlanPickerShown) {
      captureEvent(analyticsEvents.FLOOR_PLAN_SELECTION_SCREEN_VIEW)
    }
  }, [floorPlanPickerShown])

  //==========================================================================
  // START Logic for calculating largest pill width https://sparx-app.atlassian.net/browse/AT-1123
  //==========================================================================

  const largestWidthPillTarget = useRef()
  const controlTextRef = useRef()

  useEffect(() => {
    if (!single_line) {
      if (largestWidthPillTarget && largestWidthPillTarget.current && controlTextRef && controlTextRef.current) {
        controlTextRef.current.style.width = `${largestWidthPillTarget.current.offsetWidth + 18}px`
      }
    }
  }, [largestWidthPillTarget, controlTextRef, single_line])

  const largetPillWidthTargetItem = useMemo(() => {
    let largest
    lines.forEach(({ data }) => {
      const t = [
        getLineName(data),
        getHumanLabelBedrooms(data),
        getHumanLabelBathrooms(data),
        data.size_to_display ? `${data.size_to_display} ft sq` : '',
      ].join(' · ')
      if (!largest || largest.text.length < t.length) {
        largest = {
          text: t,
          data,
        }
      }
    })
    return largest.data
  }, [lines])

  function renderButtonPillText(line, ref, className = '') {
    return (
      <div className={`px-[12px] font-medium w-max lg:pr-[16px] ${className}`} ref={ref}>
        <div className={`${isSFApp ? 'text-white' : 'text-[#3D4256]'} text-[16px] w-max`}>{getLineName(line)}</div>
        <div className={`${isSFApp ? 'text-white' : 'text-[#3D4256]'} opacity-70 text-[12px]`}>
          {[getHumanLabelBedrooms(line), getHumanLabelBathrooms(line)].filter((val) => val).join(' · ')}
          {line.size_to_display ? ` · ${line.size_to_display} ft sq` : ''}
        </div>
      </div>
    )
  }

  //==========================================================================
  // END Logic for calculating largest pill width https://sparx-app.atlassian.net/browse/AT-1123
  //==========================================================================

  single_line = single_line || isBrodskyApp || isSFApp

  const hasPicker = !single_line && lines.length > 1

  return (
    <>
      <div className={`${className} lg:h-max lg:z-[100] ${single_line ? 'pointer-events-none' : ''}`} ref={ref}>
        <div className="relative lg:w-max">
          {!mobile && (
            <>
              <div
                role="button"
                className={`${isSFApp ? 'bg-black' : 'bg-white'
                  } cursor-pointer shadow h-[52px] flex items-center justify-between rounded overflow-hidden`}
                onClick={() => {
                  captureEvent(analyticsEvents.FLOOR_PLAN_TAPPED)
                  hasFloorPlanPicker ? setFloorPlanPickerShown(!floorPlanPickerShown) : setToggled(!toggled)
                }}
              >
                {renderButtonPillText(selectedLine, controlTextRef)}
                {!single_line && (
                  <div
                    className={`h-full border-l-[1px] border-solid ${isSFApp ? 'border-[#545454]' : 'border-[#C7CCD9]'
                      } w-[32px] px-[12px] flex items-center justify-center cursor-pointer ${single_line ? 'opacity-0' : ''
                      }`}
                  >
                    <div className="flex imems-center justify-center flex-shrink-0">
                      <Arrow />
                    </div>
                  </div>
                )}
              </div>
              {renderButtonPillText(largetPillWidthTargetItem, largestWidthPillTarget, 'h-0 overflow-hidden')}
            </>
          )}
          {mobile && (
            <div
              className={`text-[14px] rounded-[4px]  border-[#C7CCD9] border-[1px] border-solid flex pointer`}
              role="button"
              onClick={() => {
                captureEvent(analyticsEvents.FLOOR_PLAN_TAPPED)
                hasFloorPlanPicker ? setFloorPlanPickerShown(!floorPlanPickerShown) : setToggled(!toggled)
              }}
            >
              <div className="p-[8px] landscape:w-full text-[14px] font-medium text-left portrait:w-max leading-[16px] pr-[12px] h-full">
                <div className="text-[#3D4256]">{selectedLine.name}</div>
                {/* {selectedLine.size_to_display && <div className='text-[rgba(61,66,86,.6)] mt-[5px]'>{selectedLine.size_to_display} SF</div>} */}
              </div>
              {hasPicker && (
                <div
                  className={`w-[32px] flex-shrink-0 flex items-center border-l-[1px] border-solid border-[#C7CCD9] justify-center`}
                >
                  <span className={`rotate-[-90deg] block`}>
                    <Arrow />
                  </span>
                </div>
              )}
            </div>
          )}
          {isMobile && (
            <>
              <BottomSheet
                open={toggled}
                spaceFromTop={isMobileLandscape ? 60 : undefined}
                onToggle={() => {
                  setToggled(false)
                }}
                title="Choose Floor Plan Type"
              >
                <div>
                  {lines.map((line) => (
                    <button
                      onClick={() => {
                        setToggled(false)
                        // Timeout is addded to prevent instant switching of presented items before drawer will be closed.
                        setTimeout(() => {
                          onLineSelected(line)
                        }, 100)
                      }}
                      type="button"
                      className="text-left py-[20px] font-medium text-[#3D4256] border-t-[1px] flex items-center justify-between w-full border-solid border-[#E5E7ED] first:border-t-0"
                    >
                      <TickerText key={toggled} line={line} />
                    </button>
                  ))}
                  {property?.lines_preloading && <LinesLoadingIndicator />}
                </div>
              </BottomSheet>
              <BottomSheet
                open={floorPlanPickerShown}
                spaceFromTop={isMobileLandscape ? 60 : undefined}
                onToggle={() => {
                  setFloorPlanPickerShown(false)
                  captureEvent(analyticsEvents.FLOOR_PLAN_SELECTION_SCREEN_CLOSE)
                }}
              >
                <div className="max-h-[calc(100vh-150px)] relative">
                  <FloorPlanPickerContent
                    selectedPlan={selectedPlan}
                    lines={lines}
                    onSelect={(fp, line, pack, level) => {
                      setFloorPlanPickerShown(false)
                      onFloorPlanSelected(fp, line, pack, level)
                    }}
                  />
                  {property?.lines_preloading && (
                    <div className="w-full my-[32px] opacity-80 bg-white backdrop-blur-xl flex items-center justify-center z-10">
                      <LinesLoadingIndicator size={64} />
                    </div>
                  )}
                </div>
              </BottomSheet>
            </>
          )}
          {isDesktop && (
            <>
              {hasFloorPlanPicker && (
                <FloorPlanPicker
                  shown={floorPlanPickerShown}
                  selectedPlan={selectedPlan}
                  lines={lines}
                  property={property}
                  onClose={() => setFloorPlanPickerShown(false)}
                  onSelect={(fp, line, pack, level) => {
                    setFloorPlanPickerShown(false)
                    onFloorPlanSelected(fp, line, pack, level)
                  }}
                />
              )}
              {toggled && (
                <>
                  {property?.lines_preloading ? (
                    <div className="bg-white rounded-[4px] p-[16px] font-semibold text-[14px] mt-[12px]">
                      <LinesLoadingIndicator />
                    </div>
                  ) : (
                    <div className="w-full bg-[#fff] border-[1px] border-[#C7CCD9] border-solid mt-[12px] flex flex-col overflow-hidden rounded-[6px] p-[4px] max-h-[calc(100vh-300px)] overflow-y-auto">
                      {lines.map((line, index) => (
                        <div className="bg-white">
                          <button
                            onClick={() => {
                              onLineSelected(line)
                              setToggled(false)
                            }}
                            type="button"
                            className="w-full text-[14px] hover:bg-[#E5E7ED] text-left px-[10px] font-medium text-[#3D4256] rounded-[4px] py-[4px]"
                          >
                            <div>{line.label}</div>
                            <div>
                              <div className="text-[#3D4256] opacity-40 text-[14px]">
                                {[getHumanLabelBedrooms(line.data), getHumanLabelBathrooms(line.data)].join(' · ')}
                                {line.data.size_to_display ? ` · ${line.data.size_to_display} ft sq` : ''}
                              </div>
                            </div>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </>
  )
}

export default LinePicker
